import React from 'react';

type SectionCardVariant = 'default' | 'config' | 'result';

interface SectionCardProps {
  id: string;
  title: string;
  /** Small muted line under the title — e.g. clarifying what else a section feeds into or is shared by. Omitted by default. */
  subtitle?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  /**
   * Visual weight — default (unchanged, every existing usage) is a plain
   * white card. `config` is deliberately quieter (heavier neutral border,
   * lighter title, no fill — a colored fill clashes with row-level colors
   * already inside the table) for a section that's an input/setting, not
   * an answer.
   * `result` is deliberately lifted a touch off the page (a solid, unblurred
   * drop shadow straight down — no horizontal offset, no blur radius,
   * matching the mobile app's own card treatment) and carries a small
   * rounded accent bar, inset from the left edge and spanning 85% of the
   * card's height, for a section that IS the answer (a chart, a
   * comparison). Lets a page like Farm Diagnostics visually tell the two
   * apart at a glance, regardless of scroll position, without every other
   * SectionCard on the site inheriting an opinion it never asked for.
   */
  variant?: SectionCardVariant;
  /**
   * `config` only — whether the section is currently collapsed. Expanded,
   * `config` uses white + a heavier neutral border (a fill behind the table
   * clashed with the row-level colors already inside it — zebra striping,
   * highlight borders). Collapsed, there's no table content to clash with,
   * so it gets a light fill + light border instead, reading as present but
   * idle rather than looking identical to a plain unstyled card. Ignored by
   * every other variant.
   */
  collapsed?: boolean;
}

const PADDING_LEFT_BY_VARIANT: Record<SectionCardVariant, string> = {
  default: 'pl-4',
  config: 'pl-4',
  // Extra room to clear the `result` variant's accent bar.
  result: 'pl-8',
};

// result's shadow is a solid (0 blur, 0 spread) offset straight down — no
// Tailwind default shadow utility does this (they're all blurred), so it's
// expressed as an arbitrary box-shadow value. Same color/token as the
// border itself (--color-bright-gray) — a darker shadow read as a mismatched
// hard edge sitting under a much lighter border instead of one continuous
// outline.
const getFillAndBorderClass = (variant: SectionCardVariant, collapsed: boolean): string => {
  if (variant === 'result')
    return 'border border-gray-bright bg-white shadow-[0_4px_0_0_var(--color-bright-gray)]';
  if (variant === 'config') {
    return collapsed
      ? 'border border-gray-bright bg-gray-smoke'
      : 'border-2 border-gray-medium bg-white';
  }
  return 'border border-gray-bright bg-white';
};

const TITLE_CLASS_BY_VARIANT: Record<SectionCardVariant, string> = {
  default: 'text-base font-bold text-charcoal',
  config: 'text-sm font-semibold text-gray-dark',
  result: 'text-base font-bold text-charcoal',
};

/** Shared bordered/padded shell for every Summary 2 section — gives each table clear visual separation from the next as the page scrolls. */
const SectionCard: React.FC<SectionCardProps> = ({
  id,
  title,
  subtitle,
  actions,
  children,
  variant = 'default',
  collapsed = false,
}) => (
  <section
    id={id}
    className={`relative scroll-mt-24 rounded-lg py-4 pr-6 ${PADDING_LEFT_BY_VARIANT[variant]} ${getFillAndBorderClass(variant, collapsed)}`}
  >
    {variant === 'result' && (
      // top/bottom insets (not `height`), so the bar's size is solved
      // directly from the card's actual rendered height — 7.5% + 7.5% off
      // top and bottom leaves exactly 85% for the bar, however tall the
      // card turns out to be. A `height` percentage on its own depends on
      // the ancestor having a genuinely definite height to resolve against;
      // pinning both edges avoids that entirely.
      <span className="absolute left-2 top-[7.5%] bottom-[7.5%] w-1 rounded-full bg-primary" />
    )}
    <div className="mb-3 flex flex-wrap items-center gap-3">
      <h3 className={TITLE_CLASS_BY_VARIANT[variant]}>{title}</h3>
      {actions}
    </div>
    {subtitle && <p className="-mt-2 mb-3 text-sm text-gray-dark">{subtitle}</p>}
    {children}
  </section>
);

export default SectionCard;
